import {
  AddResourceToCallRequestSchema,
  type AddResourceToCallRequest,
} from '@act/shared/types';
import { Button, FormBuilder } from '@act/storybook';
import { Box } from '@mui/material';
import { Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { callsRequests } from 'redux/requests/calls.request';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { useMemo, useEffect, useRef } from 'react';
import { useToast } from 'react-components/app/ReusableToast';

interface ResourceCallFormProps {
  callId: number;
  handleClose: () => void;
}

export const createCallFormConfig =
  (): FormConfig<AddResourceToCallRequest> => {
    return [
      {
        type: 'input',
        name: 'title',
        label: 'Resource Title',
        input: 'text',
        props: {
          required: true,
          placeholder: 'Enter resource title',
        },
      },
      {
        type: 'input',
        name: 'phoneNumber',
        label: 'Resource Phone Number',
        input: 'phone',
        props: {
          required: true,
          placeholder: 'Enter phone number',
        },
      },
    ];
  };

export const ResourceCallForm = ({
  callId,
  handleClose,
}: ResourceCallFormProps) => {
  const toast = useToast();
  const {
    control,
    formState,
    handleSubmit,
    formState: { isValid },
  } = useForm<AddResourceToCallRequest>({
    mode: 'onChange',
    resolver: zodResolver(AddResourceToCallRequestSchema),
    defaultValues: {
      title: '',
      phoneNumber: '',
    },
  });

  const [addedCall, isAddingResourceCall, addResourceToCall] = useApiCall(
    callsRequests.addResourceToCall
  );

  const formConfig = useMemo(() => createCallFormConfig(), []);

  // Track the last processed call addition to prevent duplicate processing
  const lastProcessedCallRef = useRef<typeof addedCall>(null);

  // Wait for API response before closing form
  useEffect(() => {
    // Only process if we have a new result (different from last processed)
    if (
      addedCall &&
      !isAddingResourceCall &&
      addedCall !== lastProcessedCallRef.current
    ) {
      toast('Resource added successfully', 'success');
      handleClose();
      lastProcessedCallRef.current = addedCall;
    }
  }, [addedCall, isAddingResourceCall, toast, handleClose]);

  const onSubmit = (data: AddResourceToCallRequest) => {
    addResourceToCall({
      body: data,
      params: { callId },
    });
  };
  return (
    <>
      <Typography
        variant="h6"
        color="text.primary"
        fontWeight={600}
        sx={{ marginBottom: '16px' }}
      >
        Enter resource to add to the call
      </Typography>
      <FormBuilder
        control={control}
        formState={formState}
        config={formConfig}
        disabled={isAddingResourceCall}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '16px',
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isAddingResourceCall || !isValid}
          trackingName="submit-add-resource-button"
        >
          Add Resource
        </Button>
      </Box>
    </>
  );
};
